import React, { createContext, useCallback, useRef } from 'react';
import { useFormik, FormikValues, FormikErrors, FormikHelpers, FormikProvider } from 'formik';
import { wrap } from '@hilma/tools';

import { DateRule, Direction, FileRule, FormRules, Language, LengthRule, NameRule, RangeRule, RegexRule, ValidateValueConfig } from '../types';
import { formatTime, validators } from '../functions';
import { useBaseI18n, useBaseTranslate, useDirection, useLanguage } from '../hooks';
import { confirmRegex } from '../constants';
import { get as _get } from 'lodash'
import { I18nProviderProps } from './I18nProvider';
import { GI18nSelector, GTranslatedI18n } from '../types/types/GenericTypes';


export const FormRulesContext = createContext<FormRules | null>(null);
export const FormRefsContext = createContext<React.MutableRefObject<Record<string, any>> | null>(null);
export const OnSubmitRefContext = createContext<React.MutableRefObject<((values: FormikValues, formikHelpers: FormikHelpers<FormikValues>) => void | Promise<any>) | null> | null>(null);
export const GetRuleNameContext = createContext<{ (rule: NameRule<any>): string; } | null>(null);
export const GetRuleRegexErrorContext = createContext<{ (rule: RegexRule<any>): string; } | null>(null);

interface TranslationInterface {
	usei18n: (selector?: (i18n: GTranslatedI18n) => any) => any,
	useTranslate: () => (param: string | GI18nSelector<any>) => string,
	useDirection: () => Direction,
	useLanguage: () => string | Language
}
export type TranslateAndReplaceParams = {
	path: string,
	values: Array<string | number>
} | string
interface TranslationContextInterface extends TranslationInterface {
	translateAndReplace: (params: TranslateAndReplaceParams) => string
}
export const TranslationContext = createContext<TranslationContextInterface | null>(null);

export interface FormProviderProps {
	initialValues: Partial<FormikValues>;
	refs?: Partial<FormikValues>;
	rules?: FormRules<any>;
	validationSchema?: any | (() => any),
	validateOnChange?: boolean;
	/** Tells Formik to validate the form on each input's onBlur event */
	validateOnBlur?: boolean;
	/** Tells Formik to validate upon mount */
	validateOnMount?: boolean;

	i18nOptions?: I18nProviderProps,
	customI18n?: TranslationInterface
}

const FormProvider: React.FC<FormProviderProps> = ({ i18nOptions, customI18n, validationSchema, initialValues, rules = {}, refs: r = initialValues, children, ...rest }) => {
	const refs = useRef({ ...r });
	const onSubmitRef = useRef<((values: FormikValues, formikHelpers: FormikHelpers<FormikValues>) => void | Promise<any>) | null>(null);

	const translate = customI18n ? customI18n.useTranslate() : useBaseTranslate();

	const language = customI18n ? customI18n.useLanguage() as Language : useLanguage();


	const translateAndReplace = useCallback((params: TranslateAndReplaceParams) => {
		if (typeof params === "string") return translate(params);
		if (!params) return ""
		let { path, values } = params;
		if (!path) return "";
		let value = translate(path);
		values = values || [];
		for (let param of values) {
			if (typeof param === 'string')
				param = translate(param) || param;
			value = value.replace("{?}", String(param ?? ""));//})//todo translate values?
		}
		return value;
	}, [translate]);

	const getRuleName = useCallback((rule: NameRule<any>): string => {
		return ((rule as NameRule)?.name instanceof Function ? translate(rule?.name || '') : rule?.name as string) ?? '';
	}, [translate]);

	const getRuleRegexError = useCallback((rule: RegexRule<any>) => {
		return (rule?.activeRegexErr instanceof Function ? translate(rule?.activeRegexErr || '') : rule?.activeRegexErr) ?? '';
	}, [translate]);

	const validate = useCallback(async (values: FormikValues): Promise<FormikErrors<FormikValues>> => {
		const errors: Record<string, { path: string, values: any[] }> = {};

		for (let key in rules) {
			const rule = _get(rules, key);
			const value = _get(values, key);
			if (!rule) continue;

			const config: ValidateValueConfig = { key, refs, rule, value, values };

			if (validators.isFileFromServer(config)) {
				continue;
			} else if ((rule as FileRule).accept && !validators.isFile(config)) {
				errors[`${key}Path`] = { path: "error.required", values: [] }// errorGenerators.required(language);
			}

			else if (!validators.isRequiredValue(config)) {
				errors[key] = { path: "error.required", values: [] }//errorGenerators.required(language);
			}
			else if ((rule as DateRule).before && !validators.isDateBefore(config)) {
				errors[key] = { path: 'error.dateBefore', values: [value.toLocaleDateString()] };// errorGenerators.dateBefore(language, (rule as DateRule).before!);
			}

			else if ((rule as DateRule).after && !validators.isDateAfter(config)) {
				errors[key] = { path: 'error.dateAfter', values: [value.toLocaleDateString()] };//errorGenerators.dateAfter(language, (rule as DateRule).after!);
			}

			else if ((rule as DateRule).before && !validators.isTimeBefore(config)) {
				errors[key] = { path: 'error.beforeTime', values: [formatTime(value, language)] }// errorGenerators.timeBefore(language, (rule as DateRule).before!);
			}

			else if ((rule as DateRule).after && !validators.isTimeAfter(config)) {
				errors[key] = { path: 'error.afterTime', values: [formatTime(value, language)] }// errorGenerators.timeAfter(language, (rule as DateRule).after!);
			}

			else if (!validators.isStringGreaterThan(config)) {
				errors[key] = { path: 'error.greaterThan', values: [getRuleName(rule as NameRule), (rule as RangeRule).greaterThan!] }  //errorGenerators.greaterThan(language, getRuleName(rule as NameRule), (rule as RangeRule).greaterThan!);
			}

			else if (!validators.isStringSmallerThan(config)) {
				errors[key] = { path: 'error.smallerThan', values: [getRuleName(rule as NameRule), (rule as RangeRule).greaterThan!] }// errorGenerators.smallerThan(language, getRuleName(rule as NameRule), (rule as RangeRule).smallerThan!);
			}

			else if ((rule as LengthRule).length && !validators.isStringLength(config)) {
				errors[key] = { path: 'error.length', values: [getRuleName(rule as NameRule), (rule as LengthRule).length] }//errorGenerators.length(language, getRuleName(rule as NameRule), (rule as LengthRule).length);
			}


			else if ((rule as RegexRule).activeRegex && !validators.isMatchActiveRegex(config)) {//todo
				errors[key] = { path: 'error.regex', values: [getRuleName(rule as NameRule) || translate('error.thisField'), (rule as RangeRule).greaterThan! || translate('error.invalid')] }//errorGenerators.regex(language, (rule as NameRule).name && getRuleName(rule as NameRule), (rule as RegexRule).activeRegexErr && getRuleRegexError(rule as RegexRule));
			}

			else if (!validators.isMatchRegexClassValidator(config)) {//todo
				errors[key] = { path: 'error.regex', values: [(rule as NameRule).name && getRuleName(rule as NameRule) || translate('error.thisField'), (rule as RegexRule).activeRegexErr && getRuleRegexError(rule as RegexRule) || translate('error.invalid')] }
			}

			else if (!validators.isConfirmValid(config)) {
				errors[key] = { path: "error.confirm", values: [] }
				errors[key.match(confirmRegex)![0].toLowerCase().substring(7, key.match(confirmRegex)![0].toLowerCase().length)] = { path: "error.confirm", values: [] }
			}
		}

		return errors as FormikErrors<FormikValues>;
	}, [language, getRuleName, getRuleRegexError, rules]);

	const formik = useFormik({
		initialValues: initialValues as FormikValues,
		validate: !validationSchema ? validate : undefined,
		validationSchema: validationSchema,
		onSubmit: onSubmitRef.current || (() => { }),
		...rest
	});
	return wrap(
		[TranslationContext.Provider, {
			value: customI18n ? { ...customI18n, translateAndReplace } :
				{
					usei18n: useBaseI18n,
					useTranslate: useBaseTranslate,
					useDirection: useDirection,
					useLanguage: useLanguage,
					translateAndReplace
				}
		}],
		[FormikProvider, { value: formik }],
		[FormRulesContext.Provider, { value: rules }],
		[FormRefsContext.Provider, { value: refs }],
		[OnSubmitRefContext.Provider, { value: onSubmitRef }],
		[GetRuleNameContext.Provider, { value: getRuleName }],
		[GetRuleRegexErrorContext.Provider, { value: getRuleRegexError }]
	)(children) as React.ReactElement;
}

export default FormProvider;